import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
    AccountActions,
    AccountSelectors,
    ModulesSelectors,
    SlideToggleComponent,
} from '@serviceos-ng/core';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { finalize, tap, filter } from 'rxjs/operators';
import { Preferences } from '../communication-preferences';

@Component({
    selector: 'serviceos-web-preferences-form',
    templateUrl: './preferences-form.component.html',
    styleUrls: ['./preferences-form.component.scss'],
})
export class PreferencesFormComponent
    implements OnInit, OnDestroy, AfterViewInit {
    @Select(AccountSelectors.preferences)
    public preferences!: Observable<
        | {
              allow_mk_sms?: boolean;
              allow_mk_email?: boolean;
              allow_mk_call?: boolean;
              allow_mk_push_notification?: boolean;
              allow_mk_letter?: boolean;
              preferences_submitted?: boolean;
          }
        | null
        | []
    >;

    @Select(
        ModulesSelectors.getComponentFromModule(
            'account',
            'account_settings_screen.communication_preferences.preferences'
        )
    )
    preferencesConfig$!: Observable<Preferences | null>;
    preferencesConfig: Preferences | null = null;

    @ViewChildren(SlideToggleComponent)
    slideToggles!: QueryList<SlideToggleComponent>;

    public mappedPreferences; // used to iterate easily and generate template containers
    public filteredPreferences; // used as structure for updating preferences

    public subscriptions: Subscription[] = [];
    public showLoader = false;

    constructor(private _store: Store) {}

    ngOnInit(): void {
        this.subscriptions.push(
            combineLatest([this.preferences, this.preferencesConfig$])
                .pipe(
                    filter(([preferences, preferencesConfig]) => {
                        if (preferences && preferencesConfig) {
                            return true;
                        } else {
                            return false;
                        }
                    }),
                    tap(([preferences, preferencesConfig]) => {
                        this.preferencesConfig = preferencesConfig;
                        if (preferences) {
                            // filter preferences object by leaving only input fields
                            this.filteredPreferences = Object.keys(preferences)
                                .filter((preferenceKey: string) =>
                                    preferenceKey.includes('allow')
                                )
                                .reduce((obj, key) => {
                                    return Object.assign(obj, {
                                        [key]: preferences[key],
                                    });
                                }, {});

                            // map preferences in order to iterate them easily in view
                            this.mappedPreferences = Object.keys(preferences)
                                .filter((preferenceKey: string) =>
                                    preferenceKey.includes('allow')
                                )
                                .reduce(
                                    (
                                        accumulator: Array<any>,
                                        currentKey: string,
                                        currentIndex: number
                                    ) => {
                                        if ((currentIndex + 1) % 2 !== 0) {
                                            accumulator.push([
                                                {
                                                    key: currentKey,
                                                    value:
                                                        preferences[currentKey],
                                                    label: this.generateLabel(
                                                        currentKey
                                                    ),
                                                },
                                            ]);
                                        } else {
                                            accumulator[
                                                accumulator.length - 1
                                            ].push({
                                                key: currentKey,
                                                value: preferences[currentKey],
                                                label: this.generateLabel(
                                                    currentKey
                                                ),
                                            });
                                        }

                                        return accumulator;
                                    },
                                    []
                                );
                        }
                    })
                )
                .subscribe()
        );
    }

    ngAfterViewInit() {
        this.registerHandlers();

        this.slideToggles.changes.subscribe((r) => {
            this.registerHandlers();
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.map((el) => el.unsubscribe());
    }

    /**
     * Method which registers handelrs for earch slide toggle input
     */
    private registerHandlers(): void {
        if (this.slideToggles) {
            this.slideToggles.map((toggle) => {
                toggle.registerOnChange((value) => {
                    if (toggle.name) {
                        this.filteredPreferences[toggle.name] = value;

                        this.showLoader = true;

                        this._store
                            .dispatch(
                                new AccountActions.UpdatePreferences(
                                    this.filteredPreferences
                                )
                            )
                            .pipe(
                                finalize(() => {
                                    this.showLoader = false;
                                })
                            )
                            .subscribe();
                    }
                });
            });
        }
    }

    /**
     * Generate static labels for each preference
     * @param inputKey preference key
     * @returns
     */
    private generateLabel(inputKey: string): string {
        let label: string;

        switch (inputKey) {
            case 'allow_mk_call':
                label =
                    this.preferencesConfig?.allow_mk_call_input_label ||
                    'Phone';

                break;
            case 'allow_mk_email':
                label =
                    this.preferencesConfig?.allow_mk_email_input_label ||
                    'E-mail';

                break;
            case 'allow_mk_letter':
                label =
                    this.preferencesConfig?.allow_mk_letter_input_label ||
                    'Mail';

                break;
            case 'allow_mk_push_notification':
                label =
                    this.preferencesConfig
                        ?.allow_mk_push_notification_input_label ||
                    'Push notification';

                break;
            case 'allow_mk_sms':
                label =
                    this.preferencesConfig?.allow_mk_sms_input_label || 'Text';

                break;
            default:
                label = 'Preference';

                break;
        }

        return label;
    }
}
